import { Button, Card, DatePicker, RadioGroup, RadioGroupItem } from "@dune/ui";
import { formatSimpleDate } from "@dune/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import type { DateValue } from "@internationalized/date";
import { parseDate } from "@internationalized/date";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
	useUpdateConstructionSiteReception,
	useDeleteFileFromConstructionSiteReception,
} from "../../../../api/mutations/constructionSiteSubmission.mutations";
import {
	useGetConstructionSiteReception,
	useGetConstructionSiteSubmission,
} from "../../../../api/queries/constructionSiteSubmission.queries";
import { useRequiredParams } from "../../../../hooks/useRequiredParams";
import FileDropUpload from "../../../../component/common/files/FileDropUpload";
import { useNavigate } from "react-router-dom";
import { PAGES } from "@dune/types";
import { useLinkFileToConstructionSiteReception } from "../../../../api/mutations/constructionSiteReception.mutations";

const AUTHORIZATION_DOCUMENT_TYPES = {
	PERMIS_CONSTRUIRE: "PERMIS_CONSTRUIRE",
	DECLARATION_PREALABLE: "DECLARATION_PREALABLE",
} as const;

const receptionFormSchema = z.object({
	plannedDate: z.string(),
	receptionDate: z.custom<DateValue>().nullable(),
	hasAuthorizationChange: z.boolean(),
});

type ReceptionFormData = z.infer<typeof receptionFormSchema>;

type Props = { onNext: () => void };

export const ReceptionStep1 = ({ onNext }: Props) => {
	const { constructionSiteId } = useRequiredParams<{
		constructionSiteId: string;
	}>();
	const numericId = useMemo(
		() => Number(constructionSiteId),
		[constructionSiteId],
	);
	const navigate = useNavigate();

	const { data: receptionData } = useGetConstructionSiteReception(numericId);
	const { data: submission } = useGetConstructionSiteSubmission(numericId);
	const { mutate: saveReception, isPending: isSaving } =
		useUpdateConstructionSiteReception(numericId);
	const { mutate: linkFile } =
		useLinkFileToConstructionSiteReception(numericId);
	const { mutate: deleteFile } =
		useDeleteFileFromConstructionSiteReception(numericId);

	const [fileIds, setFileIds] = useState<string[]>([]);

	const initializedRef = useRef(false);

	const form = useForm<ReceptionFormData>({
		resolver: zodResolver(receptionFormSchema),
		defaultValues: {
			plannedDate: "",
			receptionDate: null,
			hasAuthorizationChange: false,
		},
	});

	useEffect(() => {
		if (receptionData) {
			const hasAuthFromServer = Boolean(receptionData.hasAuthorizationChange);
			const preservedHasAuth = initializedRef.current
				? form.getValues("hasAuthorizationChange")
				: hasAuthFromServer;

			form.reset({
				plannedDate: receptionData.plannedDate
					? receptionData.plannedDate.slice(0, 10)
					: "",
				hasAuthorizationChange: preservedHasAuth,
				receptionDate: receptionData.receptionDate
					? parseDate(receptionData.receptionDate.slice(0, 10))
					: null,
			});

			initializedRef.current = true;

			const validFileIds =
				receptionData.filesWithType
					?.filter(
						(f) =>
							f.type === AUTHORIZATION_DOCUMENT_TYPES.PERMIS_CONSTRUIRE ||
							f.type === AUTHORIZATION_DOCUMENT_TYPES.DECLARATION_PREALABLE,
					)
					.map((f) => f.fileId) ??
				receptionData.fileIds ??
				[];
			setFileIds(validFileIds);
		}
	}, [receptionData, form]);

	const handleUploadedFiles = (uploadedFileIds: string[]) => {
		const getDocumentType = () => {
			if (
				submission?.administrativeAuthorizationType ===
				AUTHORIZATION_DOCUMENT_TYPES.DECLARATION_PREALABLE
			) {
				return AUTHORIZATION_DOCUMENT_TYPES.DECLARATION_PREALABLE;
			}

			return AUTHORIZATION_DOCUMENT_TYPES.PERMIS_CONSTRUIRE;
		};

		const documentType = getDocumentType();

		for (const fileId of uploadedFileIds) {
			if (!fileIds.includes(fileId)) {
				linkFile({
					fileId,
					type: documentType,
				});
			}
		}
		setFileIds((prev) => [...prev, ...uploadedFileIds]);
	};

	const handleDeleteFile = (fileId: string) => {
		deleteFile(fileId, {
			onSuccess: () => {
				setFileIds((prev) => prev.filter((id) => id !== fileId));
			},
		});
	};

	return (
		<div className="flex flex-col gap-6">
			<h2 className="text-xl font-semibold text-text-main-blue">
				Opération | Réception
			</h2>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<Card className="p-6">
					<div className="space-y-2">
						<div className="text-sm text-gray-700">
							Date de réception prévisionnelle
						</div>
						<input
							className="border rounded-lg h-11 px-4 w-full bg-neutral-100"
							value={
								form.watch("plannedDate")
									? formatSimpleDate(form.watch("plannedDate"))
									: ""
							}
							readOnly
						/>
					</div>
				</Card>

				<Card className="p-6">
					<DatePicker
						label="Date de réception"
						value={form.watch("receptionDate")}
						onChange={(value) => form.setValue("receptionDate", value)}
					/>
				</Card>
			</div>

			<Card className="p-6 space-y-4">
				<div className="text-sm text-gray-700">
					L'opération a-t-elle fait l'objet d'une modification d'autorisation en
					cours de chantier ?
				</div>
				<RadioGroup
					className="flex gap-6"
					value={form.watch("hasAuthorizationChange") ? "yes" : "no"}
					onValueChange={(v) =>
						form.setValue("hasAuthorizationChange", v === "yes")
					}
				>
					<RadioGroupItem value="yes" label="Oui" />
					<RadioGroupItem value="no" label="Non" />
				</RadioGroup>

				{form.watch("hasAuthorizationChange") && (
					<div className="space-y-2">
						<div className="text-sm text-gray-700">
							Joindre le document relatif à la modification de l'autorisation
						</div>
						{receptionData?.receptionFolderId && (
							<FileDropUpload
								folderId={receptionData.receptionFolderId}
								existingFileIds={fileIds}
								onUploadedFiles={handleUploadedFiles}
								onDeleteOneFile={handleDeleteFile}
							/>
						)}
					</div>
				)}
			</Card>

			<div className="flex items-center justify-between">
				<div />
				<div className="flex gap-2">
					<Button
						variant="outline"
						onClick={form.handleSubmit((data) =>
							saveReception(
								{
									receptionDate: data.receptionDate
										? data.receptionDate.toString()
										: null,
									hasAuthorizationChange: data.hasAuthorizationChange,
								},
								{
									onSuccess: () => {
										navigate(
											PAGES.PROJECTS.show.withParams(constructionSiteId),
										);
									},
								},
							),
						)}
						disabled={isSaving}
					>
						Enregistrer et fermer
					</Button>
					<Button
						onClick={form.handleSubmit((data) =>
							saveReception(
								{
									receptionDate: data.receptionDate
										? data.receptionDate.toString()
										: null,
									hasAuthorizationChange: data.hasAuthorizationChange,
								},
								{ onSuccess: () => onNext() },
							),
						)}
						disabled={!form.watch("receptionDate") || isSaving}
					>
						Suivant
					</Button>
				</div>
			</div>
		</div>
	);
};

export default ReceptionStep1;
